import type { ActionTypes, Status, TypedValidate, Types, Values } from '$types/form';
import type { SubmitFunction } from '@sveltejs/kit';
import { readable, writable } from 'svelte/store';
import { globalError } from '$lib/stores';
import { applyAction } from '$app/forms';

export const read = <T>(request: Request) =>
  request
    .formData()
    .then((data) => Object.fromEntries(data))
    .then((entries) => entries as T);

export const submit =
  (onError: () => void) =>
  <T extends ActionTypes>(types: T, validate: TypedValidate<T>) =>
  (action: keyof T): SubmitFunction =>
  ({ formData, cancel }) => {
    const values = readData(formData, types[action]);
    const error = validate[action](values as Values<T[keyof T]>);
    if (error !== null) {
      globalError.set(error.message);
      onError();
      cancel();
    }
    return async ({ result, update }) => {
      await applyAction(result);
      if ('data' in result && typeof result.data === 'object' && 'message' in result.data) {
        globalError.set(result.data.message as string);
        onError();
      }
      update({ reset: false });
    };
  };

export enum Type {
  text = 'text',
  number = 'number',
  file = 'file',
  date = 'date',
  boolean = 'boolean'
}

export const readData = <T extends Record<string, Type>>(
  data: FormData,
  types: Partial<T> = {}
): Values<T> => {
  const result: Record<string, Types> = {};
  Array.from(data.entries()).forEach(([key, value]) => {
    const type = types[key] ?? (value instanceof File ? Type.file : Type.text);
    switch (type) {
      case Type.text:
        result[key] = value as string;
        break;
      case Type.number:
        result[key] = Number(value);
        break;
      case Type.date:
        result[key] = new Date(value as string);
        break;
      case Type.file:
        result[key] = value as File;
        break;
      case Type.boolean:
        result[key] = value === 'on';
        break;
    }
  });
  return result as Values<T>;
};

export const readRequest = <T extends Record<string, Type>>(
  request: Request,
  types: Partial<T> = {}
) => request.formData().then((data) => readData(data, types));

export class FormStatus {
  private status = writable<Status>('none');

  public get loading() {
    return readable(false, (set) =>
      this.status.subscribe((status) => {
        set(status === 'loading');
      })
    );
  }

  public get success() {
    return readable(false, (set) =>
      this.status.subscribe((status) => {
        set(status === 'success');
      })
    );
  }

  public get error() {
    return readable(false, (set) =>
      this.status.subscribe((status) => {
        set(status === 'error');
      })
    );
  }

  public start() {
    this.status.set('loading');
  }

  public finish(result: boolean) {
    this.status.set(result ? 'success' : 'error');
    setTimeout(() => this.status.set('none'), 1600);
  }

  public async listen() {
    const result = await new Promise<boolean>((resolve) => {
      this.status.subscribe((status) => {
        if (status === 'success' || status === 'error') {
          resolve(status === 'success');
        }
      });
    });
    await new Promise<void>((resolve) => {
      this.status.subscribe((status) => {
        if (status === 'none') {
          resolve();
        }
      });
    });
    return result;
  }
}
